import { FC, useContext, useEffect } from 'react';
import { DocumentRequest, DocumentRequestStatus, DocumentRequestUser } from '@typings/document-api-models';
import s from '@components/UserDocumentRequestCard/UserDocumentRequestCard.module.scss';
import Buttons from '@components/Buttons';
import { useRouter } from 'next/router';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import FileDashed from '@components/Icons/FileDashed';
import Clock from '@components/Icons/Clock';
import File from '@components/Icons/File';
import CheckCircle from '@components/Icons/CheckCircle';
import WarningCircle from '@components/Icons/WarningCircle';
import { DashboardContext } from '@layouts/DashboardLayout';

type Props = {
  request: Pick<DocumentRequestUser, 'id' | 'status'> & {
    documentRequest: Pick<DocumentRequest, 'id' | 'title'>;
    actionRequiredFiles: number;
    uploadedRequiredFiles: number;
    totalRequiredFiles: number;
    uploadedFiles: number;
    totalFiles: number;
  };
  canClick: boolean;
  creatorSlug?: string;
  setIsFulfilled?: (f: boolean) => void;
};

const UserDocumentRequestCard: FC<Props> = ({ setIsFulfilled, creatorSlug, request, canClick }) => {
  const router = useRouter();
  const { creator } = useContext(DashboardContext);

  const handleClick = (): void => {
    router.push(
      getUrl(IDENTIFIERS.DOCUMENT_REQUEST_USER_DETAIL, {
        creatorSlug: creatorSlug || creator?.slug || (router.query.creatorSlug as string) || '',
        documentRequestUserId: request.id,
      }),
    );
  };

  const isAllRequiredUploaded = request.uploadedRequiredFiles === request.totalRequiredFiles;
  const noRequiredFiles = request.totalRequiredFiles === 0;

  const isUnderReview = isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.IN_REVIEW;
  const isActionRequired = request.actionRequiredFiles > 0;

  useEffect(() => {
    setIsFulfilled?.(isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.SUCCEEDED);
  }, [isAllRequiredUploaded, noRequiredFiles, request.status, setIsFulfilled]);

  return (
    <Buttons className={s.container} key={request.id} isFullWidth isWrapper onClick={handleClick} disabled={!canClick}>
      <figure style={(isAllRequiredUploaded && { backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }) || {}}>
        {!isAllRequiredUploaded && (
          <svg className={s.progress} width={44} height={44} viewBox="0 0 46 46">
            <circle
              cx={23}
              cy={23}
              r={22}
              style={{
                strokeDasharray: '138',
                strokeDashoffset: `${((request.totalRequiredFiles - request.uploadedRequiredFiles) / request.totalRequiredFiles) * 138}`,
              }}
            />
          </svg>
        )}
        {isAllRequiredUploaded
          ? (
            <File weight="fill" size={24} color="var(--color-gray-0)" />
          )
          : (
            <FileDashed size={24} color="var(--color-gray-100)" />
          )}
        {isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.SUCCEEDED && (
          <div className={s.complete}>
            <CheckCircle size={16} weight="fill" color="var(--color-success)" />
          </div>
        )}
        {isUnderReview && !noRequiredFiles && isAllRequiredUploaded && (
          <div className={s.complete}>
            <Clock size={16} weight="fill" color="var(--color-gray-100)" />
          </div>
        )}
        {isActionRequired && (
          <div className={s.complete}>
            <WarningCircle size={16} weight="fill" color="var(--color-warning)" />
          </div>
        )}
      </figure>
      <div>
        <p className="caption">Document request</p>
        <p className="body-sm-bold">{request.documentRequest?.title}</p>
        {noRequiredFiles
          ? (
            <p className="caption">
              {request.totalFiles} optional document{request.totalFiles > 1 ? 's' : ''}{' '}
              {isUnderReview && request.uploadedFiles > 0 ? 'under review' : ''}
            </p>
          )
          : (
            <p className="caption">
              {isUnderReview && creator && (
                <>
                  <strong style={{ color: 'var(--color-warning)' }}>Review required by you</strong> ·{' '}
                </>
              )}
              {(isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.IN_PROGRESS
                && !isActionRequired && !creator && (
                <>
                  All required documents uploaded. <strong style={{ color: 'var(--color-warning)' }}>Please submit for review</strong>
                </>
              )) || (
                <>
                  {!isUnderReview && `${request.uploadedRequiredFiles} of `}
                  {request.totalRequiredFiles} required document{request.totalRequiredFiles > 1 ? 's' : ''}{' '}
                  {isUnderReview && request.uploadedFiles > 0 ? 'under review' : 'uploaded'}
                </>
              )}
            </p>
          )}
      </div>
      {canClick && <Buttons isExtraSmall isThird className={s.open}>View</Buttons>}
    </Buttons>
  );
};

export default UserDocumentRequestCard;
